// Copyright Open Trade Technology Ltd. 2024. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export interface LiquidityAssetCTO {
  uuid: string
  isLytNative: boolean
  chain: string
  chainId: number
  address: EthAddr
  depositAddress: EthAddr
  symbol: string
  name: string
  decimals: number
  exchangeRate: number
  timestamp: number
  reserveTransferAddress: EthAddr
}

export const LiquidityAssetSchema = yup.object<LiquidityAssetCTO>().shape({
  uuid: yup
    .string()
    .uuid()
    .required()
    .default('')
    .label('Id')
    .meta(toFieldMetaData({ propName: 'uuid', stringType: StringTypeEnum.ID, description: 'ID of the Asset' })),
  isLytNative: yup
    .boolean()
    .required()
    .default(false)
    .label('isLytNative')
    .meta(toFieldMetaData({ propName: 'isLytNative' })),
  chain: yup
    .string()
    .required()
    .default('')
    .label('Chain')
    .meta(toFieldMetaData({ propName: 'chain' })),
  chainId: yup
    .number()
    .required()
    .default(0)
    .label('Chain ID')
    .meta(toFieldMetaData({ propName: 'chainId' })),
  address: yup
    .string()
    .required()
    .default('')
    .label('Address')
    .meta(toFieldMetaData({ propName: 'address', stringType: StringTypeEnum.ETH_ADDR })),
  depositAddress: yup
    .string()
    .required()
    .default('')
    .label('Deposit Address')
    .meta(toFieldMetaData({ propName: 'depositAddress', stringType: StringTypeEnum.ETH_ADDR })),
  symbol: yup
    .string()
    .required()
    .default('')
    .label('Symbol')
    .meta(toFieldMetaData({ propName: 'symbol' })),
  name: yup
    .string()
    .required()
    .default('')
    .label('Name')
    .meta(toFieldMetaData({ propName: 'name' })),
  decimals: yup
    .number()
    .required()
    .default(6)
    .label('Decimals')
    .meta(toFieldMetaData({ propName: 'decimals' })),
  exchangeRate: yup
    .number()
    .required()
    .default(1)
    .label('Exchange Rate')
    .meta(toFieldMetaData({ propName: 'exchangeRate', stringType: StringTypeEnum.EXCHANGE_RATE })),
  timestamp: yup
    .number()
    .required()
    .default(0)
    .label('Timestamp')
    .meta(toFieldMetaData({ propName: 'timestamp', stringType: StringTypeEnum.UNIX_DATE })),
  reserveTransferAddress: yup
    .string()
    .required()
    .default('')
    .label('Reserve Transfer Address')
    .meta(toFieldMetaData({ propName: 'reserveTransferAddress', stringType: StringTypeEnum.ETH_ADDR })),
})

export interface LiquidityAssetCTOTypeCheck extends LiquidityAssetCTO, InferType<typeof LiquidityAssetSchema> {}
